import { differenceInMinutes } from 'date-fns';
import { Either, Left, Right } from 'purify-ts';
import {
  IStorageRepository,
  Key,
  ObjectType,
  StorageFailure,
  StoredObject,
  cacheTime,
} from '../../../domain/storage';

export class StorageRepository implements IStorageRepository {
  public getStorage<T extends Key>(key: T): Either<StorageFailure, ObjectType<T>> {
    const result = localStorage.getItem(key);

    if (!result) {
      return Left(StorageFailure.thisKeyIsNotDefinedYet());
    }

    const parsedObject: StoredObject<T> = JSON.parse(result);

    if (this.isExpired(key, parsedObject.updatedDate)) {
      this.deleteFromStorage(key);
      return Left(StorageFailure.cacheExpired());
    }

    return Right(parsedObject.object as ObjectType<T>);
  }

  public setStorage<T extends Key>(key: T, data: ObjectType<T>): void {
    const storedObject: StoredObject<T> = {
      updatedDate: new Date(),
      object: data,
    };
    localStorage.setItem(key, JSON.stringify(storedObject));
  }

  public deleteFromStorage(key: Key): void {
    localStorage.removeItem(key);
  }

  public clearAll(): void {
    localStorage.clear();
  }

  private isExpired(key: Key, updatedDate: Date): boolean {
    const cacheMinutes = cacheTime[key]?.minutes;

    if (!cacheMinutes) {
      return false;
    }

    return differenceInMinutes(new Date(), new Date(updatedDate)) > cacheMinutes;
  }
}
